import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  insightsApi,
  INSIGHT_CATEGORIES,
  INSIGHT_SOURCE_LABEL,
  INSIGHT_WEIGHTS,
  insightCategoryLabel,
} from '../api/insights'
import { intelligenceApi } from '../api/intelligence'
import type { ClientInsight, InsightWeight } from '../lib/types'
import { formatDateBR } from '../lib/format'
import { IntelligenceInbox } from './IntelligenceInbox'
import { Spinner } from './Spinner'

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50'

function weightBadge(weight: InsightWeight): { label: string; cls: string } {
  return weight === 'absolute'
    ? { label: 'Verdade absoluta', cls: 'bg-amber-100 text-amber-800' }
    : { label: 'Contexto', cls: 'bg-slate-100 text-slate-600' }
}

const todayISO = () => new Date().toISOString().slice(0, 10)
const isExpired = (i: ClientInsight) => !!i.validUntil && i.validUntil.slice(0, 10) < todayISO()

/**
 * DOSSIÊ DO CLIENTE — o que a IA sabe dele, por seção. Fatos confirmados entram em
 * TODAS as análises (fase 1, fase 2, chat, tendências, quinzenal); os que a IA
 * aprendeu do retorno do gerente ficam no topo esperando confirmação.
 */
export function ClientInsights({ clientId, disabled }: { clientId: string; disabled?: boolean }) {
  const qc = useQueryClient()
  const queryKey = ['client-insights', clientId]
  const invalidate = () => {
    qc.invalidateQueries({ queryKey })
    qc.invalidateQueries({ queryKey: ['knowledge'] })
    qc.invalidateQueries({ queryKey: ['client-interviews'] })
    qc.invalidateQueries({ queryKey: ['intelligence-inbox'] })
  }

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => insightsApi.list(clientId),
  })
  const insights = data?.insights ?? []
  const live = insights.filter((i) => i.status === 'confirmed' && i.active && !isExpired(i))
  const inactive = insights.filter((i) => i.status === 'confirmed' && (!i.active || isExpired(i)))
  const rejected = insights.filter((i) => i.status === 'rejected')
  const coreMissing = INSIGHT_CATEGORIES.filter((c) => c.core && !live.some((i) => i.category === c.value))

  const [content, setContent] = useState('')
  const [category, setCategory] = useState('restricao')
  const [weight, setWeight] = useState<InsightWeight>('context')
  const [validUntil, setValidUntil] = useState('')
  const [showOld, setShowOld] = useState(false)

  const create = useMutation({
    mutationFn: () =>
      insightsApi.create(clientId, { content, category, weight, validUntil: validUntil || null }),
    onSuccess: () => {
      setContent('')
      setWeight('context')
      setValidUntil('')
      invalidate()
    },
  })

  const curate = useMutation({
    mutationFn: () => intelligenceApi.curate(clientId),
    onSuccess: invalidate,
  })

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        O que a IA <strong>sempre</strong> considera sobre este cliente. <em>Contexto</em> ajuda a interpretar
        os números; uma <em>Verdade absoluta</em> é regra do negócio e pode contrariar a leitura só de métricas
        (ex.: não negativar um termo de CPA alto por causa do LTV). A IA também aprende sozinha com os comentários,
        motivos, achados e conversas — e pede para você confirmar.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/clients/chat?client=${clientId}&mode=interview`}
          className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
        >
          🎤 Entrevista do dossiê (10 min)
        </Link>
        <button
          type="button"
          onClick={() => curate.mutate()}
          disabled={curate.isPending}
          title="Lê o retorno do gerente ainda não lido (o histórico inteiro, na primeira vez) e propõe fatos e perguntas."
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {curate.isPending && <Spinner className="h-3.5 w-3.5" />}
          {curate.isPending ? 'Aprendendo com o histórico…' : 'Aprender com o histórico agora'}
        </button>
        {curate.isSuccess && (
          <span className="text-xs text-emerald-700">
            {curate.data.ran
              ? `${curate.data.facts} fato(s) e ${curate.data.questions} pergunta(s) novos — confira abaixo.`
              : 'Nada novo para aprender.'}
          </span>
        )}
        {coreMissing.length > 0 && (
          <span className="text-xs text-slate-400">
            Falta: {coreMissing.map((c) => c.label).join(', ')}
          </span>
        )}
      </div>

      <IntelligenceInbox clientId={clientId} compact limit={20} />

      {!disabled && (
        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={2}
            placeholder='Ex.: "transporte anvisa" tem CPA alto e pouca conversão, mas cada fechamento (~1/mês) paga todo o tráfego — não negativar.'
            className={inputCls}
          />
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs font-medium text-slate-500">
              Seção
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 block rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              >
                {INSIGHT_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-500">
              Peso
              <select
                value={weight}
                onChange={(e) => setWeight(e.target.value as InsightWeight)}
                className="mt-1 block rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              >
                {INSIGHT_WEIGHTS.map((w) => (
                  <option key={w.value} value={w.value}>
                    {w.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-500" title="Para fatos temporários (ex.: promoção, verba congelada)">
              Vale até (opcional)
              <input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                // Fica dentro do <form> do cadastro: Enter não pode salvar o cliente.
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                className="mt-1 block rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={create.isPending || !content.trim()}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {create.isPending && <Spinner className="h-4 w-4 text-white" />}
              Adicionar
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            {INSIGHT_CATEGORIES.find((c) => c.value === category)?.hint}
          </p>
        </div>
      )}

      {isLoading ? (
        <Spinner />
      ) : live.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
          Nenhum fato confirmado ainda. Use a entrevista ou "Aprender com o histórico" para começar.
        </p>
      ) : (
        <div className="space-y-4">
          {INSIGHT_CATEGORIES.map((sec) => {
            const items = live.filter((i) => i.category === sec.value)
            if (items.length === 0) return null
            return (
              <div key={sec.value}>
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{sec.label}</h4>
                <ul className="space-y-2">
                  {items.map((i) => (
                    <InsightRow key={i.id} clientId={clientId} insight={i} disabled={disabled} onChange={invalidate} />
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}

      {(inactive.length > 0 || rejected.length > 0) && (
        <div>
          <button type="button" onClick={() => setShowOld((v) => !v)} className="text-xs text-slate-500 hover:underline">
            {showOld ? 'Esconder' : 'Mostrar'} inativos/vencidos ({inactive.length}) e rejeitados ({rejected.length})
          </button>
          {showOld && (
            <ul className="mt-2 space-y-2">
              {[...inactive, ...rejected].map((i) => (
                <InsightRow key={i.id} clientId={clientId} insight={i} disabled={disabled} onChange={invalidate} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function InsightRow({
  clientId,
  insight,
  disabled,
  onChange,
}: {
  clientId: string
  insight: ClientInsight
  disabled?: boolean
  onChange: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [showWhy, setShowWhy] = useState(false)
  const [content, setContent] = useState(insight.content)
  const [category, setCategory] = useState(insight.category)
  const [weight, setWeight] = useState<InsightWeight>(insight.weight)
  const [validUntil, setValidUntil] = useState(insight.validUntil?.slice(0, 10) ?? '')

  const update = useMutation({
    mutationFn: (data: {
      content?: string
      category?: string
      weight?: InsightWeight
      active?: boolean
      validUntil?: string | null
    }) => insightsApi.update(clientId, insight.id, data),
    onSuccess: () => {
      setEditing(false)
      onChange()
    },
  })
  const remove = useMutation({
    mutationFn: () => insightsApi.remove(clientId, insight.id),
    onSuccess: onChange,
  })
  // Desfaz um "Está errado": o aprendizado volta como confirmado.
  const undoReject = useMutation({
    mutationFn: () => intelligenceApi.confirm(insight.id),
    onSuccess: onChange,
  })

  const badge = weightBadge(insight.weight)
  const faded = insight.status === 'rejected' || !insight.active || isExpired(insight)

  if (editing) {
    return (
      <li className="space-y-2 rounded-lg border border-brand-200 bg-white p-3">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          className={inputCls}
        />
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            {INSIGHT_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={weight}
            onChange={(e) => setWeight(e.target.value as InsightWeight)}
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          >
            {INSIGHT_WEIGHTS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
            title="Vale até (opcional)"
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={() => update.mutate({ content, category, weight, validUntil: validUntil || null })}
            disabled={update.isPending || !content.trim()}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Salvar
          </button>
          <button
            type="button"
            onClick={() => {
              setContent(insight.content)
              setCategory(insight.category)
              setWeight(insight.weight)
              setValidUntil(insight.validUntil?.slice(0, 10) ?? '')
              setEditing(false)
            }}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancelar
          </button>
        </div>
      </li>
    )
  }

  return (
    <li className={`rounded-lg border border-slate-200 bg-white p-3 ${faded ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 flex-1 whitespace-pre-wrap text-sm text-slate-800">{insight.content}</p>
        {!disabled && insight.status !== 'rejected' && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-medium text-brand-600 hover:underline"
            >
              Editar
            </button>
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
            >
              Excluir
            </button>
          </div>
        )}
        {!disabled && insight.status === 'rejected' && (
          <button
            type="button"
            onClick={() => undoReject.mutate()}
            disabled={undoReject.isPending}
            className="shrink-0 text-xs font-medium text-emerald-700 hover:underline disabled:opacity-50"
          >
            ↺ Estava certo
          </button>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
          {insightCategoryLabel(insight.category)}
        </span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${badge.cls}`}>{badge.label}</span>
        {insight.status === 'rejected' && (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">Rejeitado</span>
        )}
        {insight.validUntil && (
          <span className={isExpired(insight) ? 'text-rose-500' : 'text-slate-500'}>
            {isExpired(insight) ? 'venceu em' : 'vale até'} {formatDateBR(insight.validUntil)}
          </span>
        )}
        <span className="text-slate-400">
          {INSIGHT_SOURCE_LABEL[insight.source] ?? insight.source}
          {insight.createdBy?.name ? ` · ${insight.createdBy.name}` : ''}
          {insight.source === 'curator' && insight.reviewedBy?.name ? ` · confirmado por ${insight.reviewedBy.name}` : ''}
          {' · '}
          {formatDateBR(insight.createdAt)}
        </span>
        {insight.evidence && (
          <button type="button" onClick={() => setShowWhy((v) => !v)} className="text-slate-500 hover:underline">
            {showWhy ? 'esconder origem' : 'de onde veio?'}
          </button>
        )}
        {!disabled && insight.status !== 'rejected' && (
          <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-slate-500">
            <input
              type="checkbox"
              checked={insight.active}
              onChange={() => update.mutate({ active: !insight.active })}
              className="rounded text-brand-600"
            />
            Ativo
          </label>
        )}
      </div>
      {showWhy && insight.evidence && (
        <p className="mt-1.5 rounded-md bg-slate-50 px-2 py-1 text-xs italic text-slate-600">{insight.evidence}</p>
      )}
    </li>
  )
}
